import { useCallback, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { patchItem } from '../../../shared/api/requestItems.api'

const MAX_UNDO = 50

/** colId AG Grid → имя поля в API patchItem. */
const COL_TO_API_FIELD: Record<string, keyof PatchableFields> = {
  price: 'targetPrice',
  priceStock: 'priceStock',
  priceManufacturer: 'priceManufacturer',
  priceRF: 'priceRF',
  priceOther: 'priceOther',
  comment: 'comment',
  partNumber: 'partNumber',
  brand: 'brand',
  noteForProcurement: 'noteForProcurement',
  qty: 'quantity',
  datasheetUrl: 'datasheetUrl',
}

type PatchableFields = {
  partNumber: string
  brand: string | null
  quantity: number
  targetPrice: number | null
  priceStock: number | null
  priceManufacturer: number | null
  priceRF: number | null
  priceOther: number | null
  comment: string | null
  noteForProcurement: string | null
  datasheetUrl: string | null
}

type UndoEntry = {
  itemId: string
  colId: string
  oldValue: unknown
}

/**
 * Undo-стэк для редактирования позиций заявки (карточка заявки).
 *
 * Кейс: админ/менеджер случайно изменил партномер / бренд / цену в чужой
 * позиции — Ctrl+Z (Cmd+Z на Mac) откатывает последнее изменение.
 *
 * Хук слушает Ctrl/Cmd+Z, делает обратный patchItem с oldValue (которое
 * AG Grid передаёт в onCellValueChanged), инвалидирует кэш заявки.
 *
 * Ограничения:
 *  - Откатывает только текстовые поля и числа через patchItem. Колонки цен
 *    матрицы (priceStock/RF/Manufacturer/Other) откатятся в самой позиции,
 *    но «синтетическая Цена менеджера» (procurement_results) не пересоздаётся
 *    автоматически — для полного отката цены матрицы нужен отдельный механизм.
 *  - Не откатывает массовые операции (bulk-update через тулбар).
 *  - Стэк локальный, держит последние 50 действий, чистится при перезагрузке.
 */
export function useItemsUndoStack(requestId: string | undefined) {
  const stackRef = useRef<UndoEntry[]>([])
  const queryClient = useQueryClient()

  const push = useCallback((entry: UndoEntry) => {
    if (!(entry.colId in COL_TO_API_FIELD)) return
    stackRef.current.push(entry)
    if (stackRef.current.length > MAX_UNDO) stackRef.current.shift()
  }, [])

  useEffect(() => {
    const onKey = async (e: KeyboardEvent) => {
      // e.code вместо e.key — независимо от русской/английской раскладки.
      if (!(e.ctrlKey || e.metaKey) || e.code !== 'KeyZ') return
      // Если фокус в input/textarea/contentEditable — это браузерный undo текста.
      const active = document.activeElement as HTMLElement | null
      if (
        active &&
        (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' || active.isContentEditable)
      )
        return

      if (stackRef.current.length === 0) return
      const entry = stackRef.current[stackRef.current.length - 1]!
      const apiField = COL_TO_API_FIELD[entry.colId]
      if (!apiField) return

      e.preventDefault()
      stackRef.current.pop()
      try {
        await patchItem(entry.itemId, {
          [apiField]: entry.oldValue,
        } as Partial<PatchableFields>)
        await queryClient.invalidateQueries({ queryKey: ['requests', requestId] })
        await queryClient.invalidateQueries({
          queryKey: ['request-items-by-request', requestId],
        })
      } catch {
        // если PATCH упал (валидация, сеть) — возвращаем запись в стэк
        stackRef.current.push(entry)
      }
    }
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [requestId, queryClient])

  return { push }
}
